/*
 * Copyright 2026 Sony Corporation
 */

import { Color3 } from '@babylonjs/core/Maths/math.color';
import { CreateLineSystem } from '@babylonjs/core/Meshes/Builders/linesBuilder';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { LinesMesh } from '@babylonjs/core/Meshes/linesMesh';
import type { Scene } from '@babylonjs/core/scene';

import { Disposables } from './disposables';
import { RIG_LAYER } from './layers';
import { DEFAULT_OPTICS, depthOfField, footprint, type ResolvedOptics } from './camera-optics';
import type { Transform } from './types';

export interface CameraRig {
  readonly deviceId: string;
  /**
   * Position and rotation only, never scaled: the preview camera parents itself
   * here, and Babylon multiplies a parent's scale straight into the view matrix.
   */
  readonly root: TransformNode;
  setTransform: (transform: Transform) => void;
  readTransform: () => Transform;
  setSelected: (selected: boolean) => void;
  /** Rig size tracks the scene, so it is neither a speck nor the whole room. */
  setSceneRadius: (radius: number) => void;
  /** Redraws the frustum for a new lens, format or focus. A no-op when nothing changed. */
  setOptics: (optics: ResolvedOptics) => void;
  dispose: () => void;
}

export interface CameraRigOptions {
  scene: Scene;
  deviceId: string;
  transform: Transform;
  sceneRadius: number;
  /** A camera the assistant has proposed but nobody has confirmed yet. */
  pending?: boolean;
  optics?: ResolvedOptics;
}

/**
 * Rig size tracks the scene so it is neither a speck nor the whole room, with a
 * floor that keeps it clickable in a very small one.
 */
const rigScale = (sceneRadius: number): number => Math.max(sceneRadius * 0.035, 0.02);

const IDLE = Color3.FromHexString('#7dd3fc');
const SELECTED = Color3.FromHexString('#fbbf24');
/** Green reads as "about to exist" without competing with the selection colour. */
const PENDING = Color3.FromHexString('#4ade80');

/**
 * One camera in the viewport: a body and the frustum it sees through.
 *
 * Rigs are **editor-owned**. They are built from twin state and outlive every
 * scene switch, which is why nothing here is registered on a scene instance.
 */
export const createCameraRig = ({
  scene,
  deviceId,
  transform,
  sceneRadius,
  pending,
  optics = DEFAULT_OPTICS,
}: CameraRigOptions): CameraRig => {
  const own = new Disposables();

  const root = new TransformNode(`rig:${deviceId}`, scene);

  // The drawing is scaled, the rig is not. Anything parented to `root` gets the
  // camera's place and aim without inheriting how big the model happens to be.
  const visuals = new TransformNode(`rig:${deviceId}:visuals`, scene);
  visuals.parent = root;

  const idle = pending ? PENDING : IDLE;

  const material = new StandardMaterial(`rig:${deviceId}:material`, scene);
  material.diffuseColor = idle;
  material.emissiveColor = idle.scale(0.35);
  material.specularColor = Color3.Black();

  const body = MeshBuilder.CreateBox(`rig:${deviceId}:body`, { width: 1, height: 0.7, depth: 1.4 }, scene);
  body.material = material;
  body.parent = visuals;

  const lens = MeshBuilder.CreateCylinder(`rig:${deviceId}:lens`, { height: 0.5, diameter: 0.55 }, scene);
  lens.rotation.x = Math.PI / 2;
  lens.position.z = 0.85;
  lens.material = material;
  lens.parent = visuals;

  // A Babylon camera looks down +Z, which is also where the lens points. The
  // lines hang off the unscaled root, from the point the preview camera sits
  // at, and end on the focus plane: the rectangle they close is the footprint.
  let current = optics;
  let frustum: LinesMesh | undefined;
  let sharpness: LinesMesh | undefined;
  let colour = idle;
  let selected = false;

  const rectangle = (distance: number): Vector3[] => {
    const { width, height } = footprint(current, distance);
    return [
      new Vector3(-width / 2, height / 2, distance),
      new Vector3(width / 2, height / 2, distance),
      new Vector3(width / 2, -height / 2, distance),
      new Vector3(-width / 2, -height / 2, distance),
    ];
  };
  const closed = (corners: Vector3[]) => [...corners, corners[0]];

  const drawFrustum = () => {
    frustum?.dispose();
    sharpness?.dispose();

    const apex = Vector3.Zero();
    const corners = rectangle(current.optics.focus_distance_m);
    frustum = CreateLineSystem(
      `rig:${deviceId}:frustum`,
      { lines: [...corners.map((corner) => [apex, corner]), closed(corners)] },
      scene
    );

    // Where the picture is acceptably sharp. Only for the selected rig: on
    // every rig at once it is noise, and a far limit at infinity has no plane.
    const { near, far } = depthOfField(current);
    const planes = [near, far].filter((distance) => Number.isFinite(distance)).map((d) => closed(rectangle(d)));
    sharpness = CreateLineSystem(`rig:${deviceId}:dof`, { lines: planes }, scene);

    for (const lines of [frustum, sharpness]) {
      lines.parent = root;
      lines.isPickable = false;
      lines.layerMask = RIG_LAYER;
    }
    frustum.color = colour;
    sharpness.color = colour;
    sharpness.alpha = 0.5;
    sharpness.isVisible = selected;
  };
  drawFrustum();

  own.add(lens, body, material, visuals, root);

  // An annotation, not scenery: the viewport draws these, a camera preview must
  // not — a rig sits inside its own lens.
  for (const mesh of [body, lens]) {
    mesh.layerMask = RIG_LAYER;
  }

  const applyScale = (sceneRadiusNow: number) => {
    const scale = rigScale(sceneRadiusNow);
    visuals.scaling = new Vector3(scale, scale, scale);
  };
  applyScale(sceneRadius);

  const setTransform = ({ position, rotation }: Transform) => {
    root.position = new Vector3(position.x, position.y, position.z);
    root.rotation = new Vector3(rotation.x, rotation.y, rotation.z);
  };
  setTransform(transform);

  return {
    deviceId,
    root,

    setTransform,

    readTransform: () => ({
      position: { x: root.position.x, y: root.position.y, z: root.position.z },
      rotation: { x: root.rotation.x, y: root.rotation.y, z: root.rotation.z },
    }),

    setSelected: (isSelected) => {
      selected = isSelected;
      colour = isSelected ? SELECTED : idle;
      material.diffuseColor = colour;
      material.emissiveColor = colour.scale(isSelected ? 0.6 : 0.35);
      if (frustum && sharpness) {
        frustum.color = colour;
        sharpness.color = colour;
        sharpness.isVisible = isSelected;
      }
    },

    setSceneRadius: applyScale,

    setOptics: (next) => {
      if (sameOptics(next, current)) {
        return;
      }
      current = next;
      drawFrustum();
    },

    dispose: () => {
      frustum?.dispose();
      sharpness?.dispose();
      own.dispose();
    },
  };
};

const sameOptics = (a: ResolvedOptics, b: ResolvedOptics): boolean =>
  JSON.stringify([a.optics, a.sensor]) === JSON.stringify([b.optics, b.sensor]);

/**
 * Where a camera with no stored transform goes.
 *
 * Derived from the scene so a new camera lands inside a 0.4 m tabletop and a
 * 12 m room alike, and spread around the centre so several do not stack.
 */
export const spawnTransform = (index: number, center: Vector3, sceneRadius: number): Transform => {
  const angle = (index * 2 * Math.PI) / 6;
  const distance = sceneRadius * 0.6;

  return {
    position: {
      x: center.x + Math.sin(angle) * distance,
      y: center.y + sceneRadius * 0.15,
      z: center.z + Math.cos(angle) * distance,
    },
    // Facing the middle of the scene, which is where the subject is.
    rotation: { x: 0, y: angle + Math.PI, z: 0 },
  };
};
